<template>
  <div>
    <HKLText :text="$t('settings.department.eCircularCategories.hint')" />
    <Collapse
      :expand="true"
    >
      <template slot="title">
        {{ $t('settings.department.eCircularCategories.tenantCategories') }}
      </template>
      <template slot="content">
        <HKLTable
          class="categories-table"
          v-bind="tenantCategoriesTableAttrs"
        />
        <HKLButton
          v-if="isEditing"
          class="add-btn"
          action="addPlus"
          @click="() => add('tenant')"
        />
      </template>
    </Collapse>
    <Collapse
      :expand="true"
    >
      <template slot="title">
        {{ $t('settings.department.eCircularCategories.vendorCategories') }}
      </template>
      <template slot="content">
        <HKLTable
          ref="form"
          class="categories-table"
          v-bind="vendorCategoriesTableAttrs"
        />
        <HKLButton
          v-if="isEditing"
          class="add-btn"
          action="addPlus"
          @click="() => add('vendor')"
        />
      </template>
    </Collapse>
  </div>
</template>
<script>
import lodash from 'lodash'
import { computed, ref, unref, watch } from 'vue-demi'
import { whenever, not } from '@vueuse/core'
import Collapse from '@/controls/Collapse'
import FusionCheck, { SOURCE, EVENT } from '@/controls/FusionCheckRender.vue'
import HKLTable from '@/controls/Table/HKLTable.vue'
import HKLText from '@/controls/HKLText.vue'
import HKLInput from '@/controls/Input/HKLInput.vue'
import i18n from '@/utils/i18n'
import { useCategories, useUpdateCategories } from './hook'
import HKLDeleteIcon from '@/controls/Icon/HKLDeleteIcon.vue'
import HKLButton from '@/controls/Button/HKLButton.vue'
import { useRefForm } from '@/controls/Form/hook'
import { E_CIRCULAR_TYPES } from '@/modules/eCircular/consts'

export default {
  components: {
    Collapse,
    HKLTable,
    HKLButton,
    HKLText,
  },
  props: ['value', 'formOptions'],
  setup (props) {
    const formRef = useRefForm()
    const isEditing = computed(() => !unref(props).formOptions.readonly)
    const deptId = computed(() => unref(props).value.id)
    const { data, refresh } = useCategories(deptId, { autoFetchOnMounted: false })
    const { refresh: updateCategories } = useUpdateCategories(deptId)
    const formMap = {
      tenant: ref([]),
      vendor: ref([]),
    }
    let maxOrder = 0
    const refreshData = async () => {
      await refresh()
      formMap.tenant.value = unref(data).filter(item => item.type === E_CIRCULAR_TYPES.TENANT)
      formMap.vendor.value = unref(data).filter(item => item.type === E_CIRCULAR_TYPES.VENDOR)
      maxOrder = Math.max(0, ...unref(data).map(item => item.order))
    }
    watch(deptId, () => refreshData())
    whenever(not(isEditing), () => refreshData(), { immediate: true })

    const getTableAttrs = (source) => computed(() => ({
      dataSource: lodash.orderBy(unref(source), ['order'], ['asc']),
      columns: [
        {
          title: '#',
          dataIndex: 'order',
          component: {
            name: HKLText,
            attrs: (_, __, index) => {
              return { text: index + 1 }
            },
          },
        },
        {
          title: i18n.t('table.header.shortForm'),
          dataIndex: 'shortname',
          component: !unref(isEditing) ? undefined : {
            name: FusionCheck,
            attrs: (value) => ({
              component: HKLInput,
              fusioninputcheck: {
                type: 'string',
                showTip: false,
                required: true,
              },
              [SOURCE]: value,
            }),
            listeners: (_, __, index) => ({
              [EVENT]: (val) => (unref(source)[index].shortname = val),
            }),
          },
        },
        {
          title: '',
          dataIndex: 'seq',
          component: {
            name: HKLText,
            attrs: { text: '-' },
          },
        },
        {
          title: i18n.t('table.header.description'),
          dataIndex: 'name',
          component: !unref(isEditing) ? undefined : {
            name: FusionCheck,
            attrs: (value) => ({
              component: HKLInput,
              fusioninputcheck: {
                type: 'string',
                showTip: false,
                required: true,
              },
              [SOURCE]: value,
            }),
            listeners: (_, __, index) => ({
              [EVENT]: (val) => (unref(source)[index].name = val),
            }),
          },
        },
        unref(isEditing) && {
          title: '',
          dataIndex: 'hasRecords',
          component: {
            name: (v) => v ? HKLText : HKLDeleteIcon,
            listeners: (_, record) => ({
              click: () => {
                const index = unref(source).findIndex(item => item.order === record.order)
                unref(source).splice(index, 1)
              },
            }),
          },
        },
      ].filter(Boolean).map((obj) => ({ ...obj, disabledSorter: true })),
    }))
    return {
      tenantCategoriesTableAttrs: getTableAttrs(formMap.tenant),
      vendorCategoriesTableAttrs: getTableAttrs(formMap.vendor),
      form: formRef.form,
      data,
      formMap,
      isEditing,
      add: (type) => unref(formMap[type]).push({
        type: type.toUpperCase(),
        status: true,
        order: ++maxOrder,
        shortname: '',
        name: '',
      }),
      async save () {
        if (!formRef.validate()) return
        const newCates = Object.values(unref(formMap)).reduce((list, cates) => {
          return list.concat(unref(cates))
        }, [])
        await updateCategories(newCates)
      },
    }
  },
}
</script>
<style lang="scss" scoped>
.add-btn {
  margin-left: 15px;
  text-align: left;
  text-decoration: underline;
  font-style: italic;
  cursor: pointer;
}
.categories-table ::v-deep {
  .order {
    width: 80px;
  }
  .shortname {
    width: 200px;
  }
  .seq {
    width: 30px;
  }
  .action {
    width: 60px;
  }
}
</style>
